import {
  buildMergedApprovalItems,
  resolveApprovalCellContent,
  ApprovalCellContent,
} from '@safetyhub/report-composer';
import type { ApprovalLineItem, ApprovalSignatureDisplay } from '@safetyhub/report-composer';

interface ApprovalDisplayTableProps {
  approvalLines: ApprovalLineItem[];
  approvalSignatures: ApprovalSignatureDisplay[];
  isOverflow: boolean;
  authorSignatureData?: string;
  authorSignerName?: string;
  onAuthorSignatureClick?: () => void;
  /** true이면 결재란 박스 하단 이름 행에서 1단계(안전관리자 lock) 이름을 표시하지 않음 */
  hideFirstAssigneeName?: boolean;
}

export function ApprovalDisplayTable({
  approvalLines,
  approvalSignatures,
  isOverflow,
  authorSignatureData,
  authorSignerName,
  onAuthorSignatureClick,
  hideFirstAssigneeName = false,
}: ApprovalDisplayTableProps) {
  // 결합된 결재 칸 목록 — 정의 base(author 포함) + 서명 오버레이 (레거시 fallback 포함)
  const mergedItems = buildMergedApprovalItems(
    approvalLines,
    approvalSignatures,
    authorSignatureData,
    authorSignerName,
  );

  // author 단계 칸에만 서명 클릭 핸들러를 연결 (DRAFT 작성자 서명 진입점)
  const stepperItems = mergedItems.map((item) => ({
    key: item.key,
    label: item.label,
    name: item.name,
    signatureData: item.signatureData,
    status: item.status as string,
    onClick: item.isAuthor ? onAuthorSignatureClick : undefined,
  }));

  if (isOverflow) {
    return (
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex items-start gap-0 pb-1 min-w-max">
          {stepperItems.map((step, idx) => {
            const isLast = idx === stepperItems.length - 1;
            const isDone = step.status === 'signed' || step.status === 'approved';
            const isRejected = step.status === 'rejected';
            const dotClass = isDone
              ? 'bg-green-500 border-green-500'
              : isRejected
              ? 'bg-red-500 border-red-500'
              : 'bg-white border-ink-300';
            const dotInner = isDone ? (
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : isRejected ? (
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <div className="w-1.5 h-1.5 rounded-full bg-ink-300" />
            );

            return (
              <div key={step.key} className="flex items-start min-w-0 shrink-0">
                <div
                  role={step.onClick ? 'button' : undefined}
                  tabIndex={step.onClick ? 0 : undefined}
                  aria-label={step.onClick ? `${step.label} ${step.name} 서명` : undefined}
                  className={`flex flex-col items-center w-16${step.onClick ? ' cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 rounded' : ''}`}
                  onClick={() => step.onClick && step.onClick()}
                  onKeyDown={(e) => {
                    if (!step.onClick) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      step.onClick();
                    }
                  }}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${dotClass}`}>
                    {dotInner}
                  </div>
                  <span className="text-[10px] font-medium text-ink-600 mt-1 text-center leading-tight">{step.label}</span>
                  <div className="w-12 h-8 mt-0.5 border border-line rounded flex items-center justify-center bg-surface-soft">
                    {(() => {
                      const cell = resolveApprovalCellContent(mergedItems[idx], false);
                      return (
                        <ApprovalCellContent
                          kind={cell.kind}
                          signatureData={cell.signatureData}
                          name={cell.name}
                          onClick={step.onClick}
                          size="sm"
                        />
                      );
                    })()}
                  </div>
                  <span className="text-[10px] text-ink-500 mt-0.5 truncate w-14 text-center">
                    {hideFirstAssigneeName && idx === 0 ? '' : step.name}
                  </span>
                </div>
                {!isLast && (
                  <div className="flex items-center mt-3 shrink-0">
                    <div className={`h-0.5 w-4 ${isDone ? 'bg-green-400' : 'bg-line'}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <div className="flex flex-col items-end">
        <div className="inline-flex border border-ink-800">
          <div className="border-r border-ink-800 px-2 flex items-center justify-center text-xs font-bold text-ink-700 leading-tight text-center">
            결<br />재
          </div>
          {mergedItems.map((item, index) => {
            // author 단계 칸 — DRAFT 작성자 서명 진입점 (onAuthorSignatureClick)
            const onClick = item.isAuthor ? onAuthorSignatureClick : undefined;
            const cell = resolveApprovalCellContent(item, false);

            return (
              <div
                key={item.key}
                role={onClick ? 'button' : undefined}
                tabIndex={onClick ? 0 : undefined}
                aria-label={
                  onClick
                    ? item.signatureData
                      ? '작성자 서명 수정'
                      : '작성자 서명하기'
                    : undefined
                }
                className={`w-20 text-center ${index < mergedItems.length - 1 ? 'border-r border-ink-800' : ''}${onClick ? ' cursor-pointer hover:bg-brand-50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-inset' : ''}`}
                onClick={() => onClick && onClick()}
                onKeyDown={(e) => {
                  if (!onClick) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                  }
                }}
                title={onClick ? (item.signatureData ? '클릭하여 서명 수정' : '클릭하여 서명하기') : undefined}
              >
                <div className="bg-surface-soft border-b border-ink-400 py-0.5 text-[10px] font-medium text-ink-600">
                  {item.label}
                </div>
                <div className="h-12 flex items-center justify-center">
                  <ApprovalCellContent
                    kind={cell.kind}
                    signatureData={cell.signatureData}
                    name={cell.name}
                    onClick={onClick}
                    size="lg"
                  />
                </div>
              </div>
            );
          })}
        </div>
        {/* 박스 아래 이름 행 — 상세 페이지 한정. 이름이 메인(font-medium)으로 강조 */}
        <div className="inline-flex mt-0.5">
          <div className="px-2 invisible text-xs">결</div>
          {mergedItems.map((item, index) => {
            const hideName = hideFirstAssigneeName && index === 0;
            return (
              <div key={`name-${item.key}`} className="w-20 flex items-center justify-center">
                <span className="text-[10px] font-medium text-ink-700 truncate px-0.5">
                  {hideName ? '' : item.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
